import {CommonModule} from '@angular/common';
import {
    AfterViewInit,
    Component,
    HostListener,
    inject,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
    ViewContainerRef
} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Router, RouterOutlet} from '@angular/router';
import {TelemetryService} from '@truelearn-web/shared/service';
import {combineLatest, firstValueFrom, from, Observable, Subject} from 'rxjs';
import {
    distinctUntilChanged,
    filter,
    first,
    map,
    startWith,
    switchMap,
    takeUntil,
    tap,
    withLatestFrom
} from 'rxjs/operators';

import {HeaderComponent} from 'apps/faculty/src/app/components/header/header.component';
import {PrimarySidebarComponent} from 'apps/faculty/src/app/components/sidebars/primary/primary-sidebar.component';
import {SecondarySidebarComponent} from 'apps/faculty/src/app/components/sidebars/secondary/secondary-sidebar.component';
import {InstitutionService} from 'libs/data-access/institution/src/lib/services/institution.service';
import {InstitutionContextService} from 'libs/data-access/institution/src/lib/services/institution-context.service';
import {mainQuestionContentId} from 'libs/question-viewer/feature/src/lib/models/main-question-content';
import {GlobalLoaderService} from 'libs/shared/component/src/lib/service/global-loader.service';
import {FacultyAppService} from 'libs/shared/faculty/src/lib/service/faculty-app.service';
import {FacultyAppContextService} from 'libs/shared/faculty/src/lib/service/faculty-app-context.service';
import {TitleService} from 'libs/shared/filter/src/lib/services/title.service';
import {IdentityService} from 'libs/shared/http/src/lib/services/identity/identity.service';
import {ApexNavigationService} from 'libs/shared/service/src/lib/services/apex-navigation/apex-navigation.service';
import {GoogleTagManagerService} from 'libs/shared/service/src/lib/services/google-tag-manager/google-tag-manager.service';
import {PreviousRouteService} from 'libs/shared/service/src/lib/services/route/previous-route.service';
import {UserPreferencesService} from 'libs/shared/service/src/lib/services/user-preferences/user-preferences.service';
import {FooterComponent} from 'libs/shared/ui/src/lib/components/footer/footer.component';
import {SkipToMainContentLink} from 'libs/shared/ui/src/lib/components/skip-to-main-content-link/skip-to-main-content-link';
import {ToastMessagesComponent} from 'libs/shared/ui/src/lib/components/toast-messages/toast-messages.component';
import {CollapseDirective} from 'libs/shared/util/src/lib/directives/collapse.directive';
import {environment} from '../environments/environment';
import {questionViewerParentRoute} from './app-routing.module';
import {FacultyGtmInitialize} from './google-tag-manager/faculty-gtm-initialize.model';

@Component({
    imports: [
        RouterOutlet,
        CommonModule,
        HeaderComponent,
        SecondarySidebarComponent,
        PrimarySidebarComponent,
        FooterComponent,
        ToastMessagesComponent,
        CollapseDirective,
        SkipToMainContentLink
    ],

    selector: 'app-root',
    styleUrls: ['./app.component.scss'],
    templateUrl: './app.component.html'
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
    @HostListener('window:beforeunload')
    onBeforeUnload(): void {
        this.userPreferencesService.savePreferencesBeacon();
        this.telemetryService.flushBeacon();
    }

    @ViewChild('loaderRef') loaderRef: TemplateRef<unknown>;
    unsubscriber = new Subject<void>();
    title = 'TrueLearn Institution Portal';
    homePageLink = environment.CLASSIC_URL + '/Institution/Home.aspx';
    showEnvironmentBanner: boolean;
    env: string;
    isIE = 'documentMode' in document && !!document['documentMode'];
    toastQueueSize = 4;

    readonly mainContentId = 'mainContent' as const;

    showMainContent$: Observable<'fullscreen' | 'question viewer' | 'main'> = this.router.events.pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        map((e) => {
            if (e.url.includes(questionViewerParentRoute)) {
                return 'question viewer' as const;
            } else if (e.url.includes('(fullscreen:')) {
                return 'fullscreen' as const;
            } else {
                return 'main' as const;
            }
        }),
        startWith('main' as const)
    );

    skipToMainFragment$ = this.showMainContent$.pipe(
        map((type) => {
            return type === 'question viewer' ? mainQuestionContentId : this.mainContentId;
        })
    );

    readonly isMimic$ = this.identityService.isMimicModeActivated();

    private readonly telemetryService = inject(TelemetryService);

    constructor(
        private appService: FacultyAppService,
        private googleTagManager: GoogleTagManagerService,
        private appContextService: FacultyAppContextService,
        private router: Router,
        private previousRouteService: PreviousRouteService,
        private institutionContextService: InstitutionContextService,
        private institutionService: InstitutionService,
        private titleService: TitleService,
        private viewContainerRef: ViewContainerRef,
        private apexNavigationService: ApexNavigationService,
        private identityService: IdentityService,
        private globalLoaderService: GlobalLoaderService,
        private userPreferencesService: UserPreferencesService
    ) {}

    async ngOnInit(): Promise<void> {
        this.setupPageTitle();

        this.previousRouteService.previousRoute$.pipe(takeUntil(this.unsubscriber)).subscribe();

        const tokenLoaded = this.appService.tokenLoaded$.pipe(
            first((tl) => tl),
            tap(() => this.globalLoaderService.hide(1000))
        );
        tokenLoaded.pipe(takeUntil(this.unsubscriber)).subscribe(async () => {
            this.institutionService.getLearnerText().pipe(takeUntil(this.unsubscriber)).subscribe();
        });

        tokenLoaded
            .pipe(
                // Reload preferences on institution subscription change — passes both subscription and institution node scopes
                switchMap(() => this.appContextService.contextInstitutionSubscriptionId$),
                filter((subId): subId is string => !!subId),
                distinctUntilChanged(),
                withLatestFrom(this.institutionContextService.contextInstitutionInformation$),

                switchMap(([subId, instInfo]) =>
                    from(
                        this.userPreferencesService.loadPreferences(
                            undefined,
                            subId,
                            instInfo.InstitutionId?.toString()
                        )
                    ).pipe(map(() => instInfo.InstitutionId))
                ),

                // Saved exam year/bank are institution-level - apply them only when the institution changes
                distinctUntilChanged(),
                tap(() => {
                    const prefs = this.userPreferencesService.allPreferences();
                    if (prefs.fac_exam_year_id != null) {
                        this.appContextService.setSelectedExamYearId(prefs.fac_exam_year_id.toString());
                    }
                    if (prefs.fac_exam_bank_id != null) {
                        this.appContextService.setSelectedExamBankId(prefs.fac_exam_bank_id.toString());
                    }
                }),

                takeUntil(this.unsubscriber)
            )
            .subscribe();

        this.suppressPreferenceSavingInMimic();
        this.suppressTelemetryInMimic();

        this.showEnvironmentBanner = environment.SHOW_BANNER;
        this.env = environment.NAME;

        this.homePageLink = environment.CLASSIC_URL + '/Institution/Home.aspx';

        await firstValueFrom(this.apexNavigationService.setup());
    }

    // Suppress preference saving while mimicking a regular faculty account so the mimicking user
    // does not overwrite the real user's saved preferences. Institution admin accounts are shared
    // accounts primarily accessed via mimic, so their preferences should still save normally.
    // startWith(false) keeps suppression conservative until the account loads: a mimicked account
    // is suppressed immediately and only re-enabled once it is confirmed to be an institution admin.
    private suppressPreferenceSavingInMimic(): void {
        combineLatest([
            this.isMimic$,
            this.appContextService.contextAccountInformation$.pipe(
                map((info) => !!info?.Account?.IsInstitutionAdmin),
                startWith(false)
            )
        ])
            .pipe(
                map(([isMimic, isInstitutionAdmin]) => isMimic && !isInstitutionAdmin),
                distinctUntilChanged(),
                takeUntil(this.unsubscriber)
            )
            .subscribe((suppress) => this.userPreferencesService.setSavingSuppressed(suppress));
    }

    // Nothing done while mimicking is attributed to the mimicked account's telemetry.
    private suppressTelemetryInMimic(): void {
        this.isMimic$
            .pipe(distinctUntilChanged(), takeUntil(this.unsubscriber))
            .subscribe((isMimic) => this.telemetryService.setSuppressed(isMimic));
    }

    ngAfterViewInit(): void {
        this.setUpGTM();
        this.globalLoaderService.show(this.viewContainerRef);
    }

    ngOnDestroy(): void {
        this.unsubscriber.next();
        this.unsubscriber.complete();
        this.appService.onDestroy();
    }

    private setupPageTitle(): void {
        this.router.events
            .pipe(
                takeUntil(this.unsubscriber),
                filter((event) => event instanceof NavigationEnd),
                map(() => {
                    let route: ActivatedRoute = this.router.routerState.root;
                    let routeTitle = '';
                    while (route!.firstChild) {
                        route = route.firstChild;
                    }
                    if (route.snapshot.data['title']) {
                        routeTitle = route!.snapshot.data['title'];
                    }
                    return routeTitle;
                }),
                filter((title: string) => !!title)
            )
            .subscribe((title: string) => {
                this.titleService.title = title;
                this.titleService.original = 'TrueLearn | Faculty';
                this.titleService.suffix = 'TrueLearn Faculty';
            });
    }

    private setUpGTM(): void {
        this.appContextService.contextAccountInformation$
            .pipe(
                takeUntil(this.unsubscriber),
                first(
                    (context) =>
                        !!context.Account.ExternalId &&
                        !!context.Institution?.InstitutionName &&
                        !!context.Account.Email
                )
            )
            .subscribe((context) => {
                const gtm = new FacultyGtmInitialize(
                    context.Account.ExternalId,
                    context.Account.Email,
                    `${context.Account.FirstName} ${context.Account.LastName}`,
                    // Institution data
                    context.Institution.InstitutionId,
                    context?.Institution?.InstitutionName
                );

                this.googleTagManager.setUpDataLayer(gtm);
            });
    }
}
